import { useState, useEffect, useCallback } from "react";
import type { PublicDocumentDto, PublicDocumentsResponse } from "../types";
import { useDocumentLinkContext } from "../providers/DocumentLinkContext";
import { API_ENDPOINTS, PAGINATION_DEFAULTS } from "../constants";

interface UsePublicDocumentsReturn {
  documents: PublicDocumentDto[];
  loading: boolean;
  error: string | null;
}

export function usePublicDocuments(): UsePublicDocumentsReturn {
  const { linkType, linkTargetId } = useDocumentLinkContext();
  const [documents, setDocuments] = useState<PublicDocumentDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDocuments = useCallback(async () => {
    if (!linkTargetId) return;

    setLoading(true);
    setError(null);

    try {
      const baseUrl = window._JSCONFIG.ReactAppApiCoreBaseUrl;
      const params = new URLSearchParams({
        linkType,
        linkTargetId: String(linkTargetId),
        pageSize: String(PAGINATION_DEFAULTS.PUBLIC_PAGE_SIZE)
      });

      const response = await fetch(
        `${baseUrl}${API_ENDPOINTS.PUBLIC_DOCUMENTS}?${params}`
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch documents: ${response.status}`);
      }

      const json = await response.json();
      const page: PublicDocumentsResponse = json.data ?? json;
      setDocuments(page.data ?? []);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to load documents";
      setError(message);
      console.error("PublicDocumentList: Error fetching documents", err);
    } finally {
      setLoading(false);
    }
  }, [linkType, linkTargetId]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  return { documents, loading, error };
}
